/* ============================================================
   Brick Box — wishlist

   Saved on this device (localStorage), no account needed. Three jobs:

     1. Heart buttons (.pc-fav) on every product card toggle a product.
        The product is read from the card's own /products/<handle> link,
        so hand-written and bound cards both work.
     2. The header heart shows the saved count ([data-wishlist-count]).
     3. /pages/wishlist ([data-wishlist-page]) renders the saved products
        with live price, stock and "price dropped since you saved it".

   Storage: bb_wishlist = [{ h: handle, p: price in paise when saved, t: time }]
   Prices, images and stock are always read fresh from /products/<handle>.js.
   ============================================================ */
(function brickboxWishlist() {
  'use strict';

  const KEY = 'bb_wishlist';
  const MAX_ITEMS = 100;
  const HANDLE = /^[a-z0-9][a-z0-9-]*$/i;
  const products = new Map();
  let memory = [];

  /* ---------- storage ---------- */

  function read() {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(raw) ? raw.filter((item) => item && HANDLE.test(item.h)) : [];
    } catch (error) {
      return memory;
    }
  }

  function write(list) {
    memory = list;
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (error) {
      /* Storage blocked: keep it for this page view only. */
    }
    document.dispatchEvent(new CustomEvent('bb:wishlist', { detail: { count: list.length } }));
  }

  const has = (handle) => read().some((item) => item.h === handle);

  function add(handle, entry) {
    const list = read();
    if (list.some((item) => item.h === handle) || list.length >= MAX_ITEMS) return;
    const item = entry || { h: handle, p: null, t: Date.now() };
    list.push(item);
    write(list);
    if (item.p === null) rememberPrice(handle);
  }

  function remove(handle) {
    const list = read();
    const item = list.find((entry) => entry.h === handle);
    write(list.filter((entry) => entry.h !== handle));
    return item;
  }

  /* The price at the moment of saving is what a later drop is measured against. */
  async function rememberPrice(handle) {
    const product = await fetchProduct(handle);
    if (!product) return;
    const list = read();
    const item = list.find((entry) => entry.h === handle);
    if (!item || item.p !== null) return;
    item.p = product.price;
    write(list);
  }

  async function fetchProduct(handle) {
    if (products.has(handle)) return products.get(handle);
    let product = null;
    try {
      const response = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' }
      });
      if (response.ok) product = await response.json();
    } catch (error) {
      /* Offline or removed product: treated as unavailable. */
    }
    products.set(handle, product);
    return product;
  }

  const toast = (message, undo) => {
    if (window.bbToast) window.bbToast(message, undo);
  };

  /* ---------- hearts and header count ---------- */

  function handleFor(heart) {
    const own = heart.getAttribute('data-wishlist-handle');
    if (own) return own;
    const card = heart.closest('.pc, article');
    const link = card && card.querySelector('a[href*="/products/"]');
    const match = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function paint() {
    const saved = new Set(read().map((item) => item.h));

    for (const heart of document.querySelectorAll('.pc-fav, [data-wishlist-toggle]')) {
      const handle = handleFor(heart);
      /* Sample cards with no real product behind them: a heart that cannot save anything is hidden. */
      if (!handle) {
        heart.style.display = 'none';
        continue;
      }
      if (heart._bbName === undefined) {
        heart._bbName = (heart.getAttribute('aria-label') || '').replace(/^Save\s*/i, '').trim();
      }
      const isSaved = saved.has(handle);
      const name = heart._bbName ? ' ' + heart._bbName : '';
      heart.classList.toggle('is-saved', isSaved);
      heart.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
      heart.setAttribute('aria-label', (isSaved ? 'Remove' : 'Save') + name + (isSaved ? ' from wishlist' : ' to wishlist'));
      if (heart.tagName === 'A') heart.setAttribute('role', 'button');
      const text = heart.querySelector('[data-wishlist-label]');
      if (text) text.textContent = isSaved ? 'Saved to wishlist' : 'Save to wishlist';
    }

    const count = saved.size;
    for (const badge of document.querySelectorAll('[data-wishlist-count]')) {
      badge.textContent = count;
      badge.hidden = count === 0;
    }
  }

  document.addEventListener('click', (event) => {
    const heart = event.target.closest('.pc-fav, [data-wishlist-toggle]');
    if (!heart) return;
    const handle = handleFor(heart);
    if (!handle) return;

    event.preventDefault();
    if (has(handle)) {
      const item = remove(handle);
      toast('Removed from wishlist', () => add(handle, item));
    } else {
      add(handle);
      toast('Saved to wishlist', () => remove(handle));
    }
  });

  let paintTimer = 0;
  new MutationObserver(() => {
    clearTimeout(paintTimer);
    paintTimer = setTimeout(paint, 80);
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('bb:wishlist', paint);
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) document.dispatchEvent(new CustomEvent('bb:wishlist'));
  });

  window.bbWishlist = { has, add, remove, list: read };
  paint();

  /* ---------- the wishlist page ---------- */

  const page = document.querySelector('[data-wishlist-page]');
  if (page) initPage(page);

  function initPage(root) {
    const grid = root.querySelector('.ws-grid');
    const count = root.querySelector('.ws-n');
    const noun = root.querySelector('.ws-w');
    const empty = root.querySelector('.ws-empty');
    const alert = root.querySelector('.ws-alert');
    const alertText = alert.querySelector('span');
    const sort = root.querySelector('#wsSort');
    const share = root.querySelector('#wsShare');
    const saveAll = root.querySelector('#wsSaveAll');
    const addAll = root.querySelector('#wsAll');
    const title = root.querySelector('#wsTitle');

    const sharedParam = new URLSearchParams(location.search).get('w');
    const shared = sharedParam !== null;
    let rows = [];
    const inCart = new Set();

    const money = (paise) => 'Rs. ' + (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    const esc = (text) => String(text).replace(/[&<>"']/g, (char) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]
    ));

    const canAdd = (row) => row.product && row.product.available && row.product.variants.length === 1;
    /* In the cart already: added from this page, or found in the real cart (any page, any tab). */
    const isInCart = (row) => inCart.has(row.entry.h) ||
      (canAdd(row) && !!window.bbCartQty && window.bbCartQty(row.product.variants[0].id) > 0);

    async function load() {
      grid.setAttribute('aria-busy', 'true');
      const entries = shared
        ? sharedParam.split(',').filter((handle) => HANDLE.test(handle)).slice(0, MAX_ITEMS)
            .map((handle, index) => ({ h: handle, p: null, t: Date.now() - index }))
        : read();
      const loaded = await Promise.all(entries.map((entry) => fetchProduct(entry.h)));
      rows = entries.map((entry, index) => {
        const product = loaded[index];
        const drop = product && entry.p !== null && entry.p > product.price ? entry.p - product.price : 0;
        return { entry, product, drop };
      });
      grid.removeAttribute('aria-busy');
      render();
    }

    function order() {
      const mode = sort.value;
      const price = (row) => (row.product ? row.product.price : Infinity);
      return rows.slice().sort((a, b) => {
        if (mode === 'low') return price(a) - price(b);
        if (mode === 'high') return (b.product ? b.product.price : -1) - (a.product ? a.product.price : -1);
        if (mode === 'drop') return (b.drop - a.drop) || (b.entry.t - a.entry.t);
        return b.entry.t - a.entry.t;
      });
    }

    function cardHtml(row) {
      const { entry, product, drop } = row;
      const url = '/products/' + encodeURIComponent(entry.h);
      const removeIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      const tagIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12V4h8l10 10-8 8z"/><circle cx="7.5" cy="8.5" r="1.4"/></svg>';

      if (!product) {
        return '<article class="ws bb-card is-out" data-handle="' + esc(entry.h) + '">' +
          '<div class="ws-img"><span class="bb-th ws-th"></span>' +
          (shared ? '' : '<button type="button" class="ws-x" aria-label="Remove from wishlist">' + removeIcon + '</button>') + '</div>' +
          '<div class="ws-b"><h3>No longer available</h3>' +
          '<div class="ws-tags"><span class="ws-st out">This product was removed from the store</span></div></div></article>';
      }

      let image = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>';
      if (product.featured_image) {
        const src = new URL(product.featured_image, location.origin);
        src.searchParams.set('width', '500');
        image = '<img src="' + esc(src.href) + '" alt="" loading="lazy" width="500" height="500">';
      }

      const compare = product.compare_at_price > product.price ? product.compare_at_price : 0;
      const from = product.price_varies ? 'From ' : '';
      let action;
      if (!product.available) {
        action = '<button type="button" class="bb-btn block" disabled>Sold out</button>';
      } else if (canAdd(row)) {
        const added = isInCart(row);
        action = '<button type="button" class="bb-btn block ws-add' + (added ? ' in' : '') + '" data-variant-id="' +
          product.variants[0].id + '"' + (added ? ' disabled' : '') + '>' +
          (added ? 'In your cart' : 'Add to cart') + '</button>';
      } else {
        action = '<a href="' + url + '" class="bb-btn block">Choose options</a>';
      }

      return '<article class="ws bb-card' + (product.available ? '' : ' is-out') + '" data-handle="' + esc(entry.h) + '">' +
        '<div class="ws-img"><a href="' + url + '" class="bb-th ws-th" tabindex="-1" aria-hidden="true">' + image + '</a>' +
        (drop ? '<span class="ws-drop">' + tagIcon + money(drop) + ' lower</span>' : '') +
        (shared ? '' : '<button type="button" class="ws-x" aria-label="Remove ' + esc(product.title) + ' from wishlist">' + removeIcon + '</button>') +
        '</div><div class="ws-b">' +
        (product.vendor ? '<span class="ck-br">' + esc(product.vendor) + '</span>' : '') +
        '<h3><a href="' + url + '">' + esc(product.title) + '</a></h3>' +
        '<div class="ws-pr"><b>' + from + money(product.price) + '</b>' + (compare ? '<s>' + money(compare) + '</s>' : '') + '</div>' +
        '<div class="ws-tags"><span class="ws-st' + (product.available ? '' : ' out') + '">' +
        (product.available ? 'In stock' : 'Sold out') + '</span></div>' + action + '</div></article>';
    }

    function render() {
      grid.innerHTML = order().map(cardHtml).join('');
      count.textContent = rows.length;
      noun.textContent = rows.length === 1 ? 'saved item' : 'saved items';
      empty.hidden = rows.length > 0;
      grid.hidden = rows.length === 0;

      const dropped = rows.filter((row) => row.drop > 0);
      alert.hidden = dropped.length === 0;
      if (dropped.length === 1) {
        alertText.innerHTML = '<b>Price drop:</b> ' + esc(dropped[0].product.title) + ' is ' + money(dropped[0].drop) +
          ' cheaper than when you saved it.';
      } else if (dropped.length > 1) {
        alertText.innerHTML = '<b>Price drop:</b> ' + dropped.length + ' items are cheaper than when you saved them.';
      }

      const addable = rows.filter((row) => canAdd(row) && !isInCart(row)).length;
      addAll.hidden = rows.length === 0;
      addAll.disabled = addable === 0;
      addAll.lastChild.textContent = 'Add ' + addable + ' to cart';
      share.hidden = shared || rows.length === 0;
      saveAll.hidden = !shared || rows.length === 0;
      sort.closest('.ws-sort').hidden = rows.length < 2;
    }

    async function addToCart(items) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) {
          const data = await response.json();
          toast(data.description || 'Could not add to cart.');
          return false;
        }
        if (window.bbSyncCartBadge) window.bbSyncCartBadge();
        return true;
      } catch (error) {
        toast('Network problem. Please try again.');
        return false;
      }
    }

    grid.addEventListener('click', async (event) => {
      const card = event.target.closest('.ws');
      if (!card) return;
      const handle = card.getAttribute('data-handle');

      if (event.target.closest('.ws-x')) {
        const item = remove(handle);
        card.classList.add('gone');
        rows = rows.filter((row) => row.entry.h !== handle);
        setTimeout(() => {
          render();
          title.focus();
        }, 250);
        toast('Removed from wishlist', () => {
          add(handle, item);
          load();
        });
        return;
      }

      const button = event.target.closest('.ws-add');
      if (button && !button.disabled) {
        button.disabled = true;
        if (await addToCart([{ id: Number(button.getAttribute('data-variant-id')), quantity: 1 }])) {
          inCart.add(handle);
          toast('Added to cart');
          render();
        } else {
          button.disabled = false;
        }
      }
    });

    addAll.addEventListener('click', async () => {
      const todo = rows.filter((row) => canAdd(row) && !isInCart(row));
      if (!todo.length) return;
      addAll.disabled = true;
      const ok = await addToCart(todo.map((row) => ({ id: row.product.variants[0].id, quantity: 1 })));
      if (ok) {
        todo.forEach((row) => inCart.add(row.entry.h));
        toast(todo.length + (todo.length === 1 ? ' item' : ' items') + ' added to your cart');
      }
      render();
    });

    share.addEventListener('click', async () => {
      const url = location.origin + '/pages/wishlist?w=' + read().map((item) => item.h).join(',');
      try {
        if (navigator.share) {
          await navigator.share({ title: 'My Brick Box wishlist', url });
        } else {
          await navigator.clipboard.writeText(url);
          toast('Wishlist link copied');
        }
      } catch (error) {
        /* Share sheet dismissed, or clipboard blocked. */
      }
    });

    saveAll.addEventListener('click', () => {
      rows.forEach((row) => add(row.entry.h));
      toast('Saved to your wishlist');
      location.href = '/pages/wishlist';
    });

    sort.addEventListener('change', render);
    /* The cart changed somewhere else (another tab, a card, the cart page). */
    document.addEventListener('bb:cart', () => { if (rows.length) render(); });
    document.addEventListener('bb:wishlist', (event) => {
      /* Another tab changed the list: reload it. Our own changes already updated rows. */
      if (!event.detail && !shared) load();
    });

    if (shared) {
      title.textContent = 'Shared wishlist';
      root.querySelector('.ws-note').textContent = 'A list someone shared with you.';
    }
    load();
  }
})();
